import type { PortalBucketCoverage, PortalSeriesPoint } from '../../types/portalReport.js';
// ⚠️ A LEAF IMPORTING A LEAF. `delivery.ts` pulls in types only, so this adds no
// runtime edge that could re-create the circular-init class of bug the prefs
// modules document. It is imported so the delivery RATIOS have exactly one
// derivation — see `valueAt`.
import { deliveryValueAt, type DeliveryMetricKey } from './delivery.js';
// ⚠️ Another leaf-of-leaves: `crm.ts` imports types and `series.ts`, no runtime
// edge back here. The CRM metrics join this vocabulary so every rule in this
// file applies to them by construction.
import type { CrmMetricKey } from './crm.js';
import type { WebsiteMetricKey } from '../ga4/websiteSeries.js';

// CLIENT PORTAL RESTYLE — the ONE chart's pure core.
//
// The report has four chart cards and they are ONE component with ONE set of
// three switches — grain, metric, form — because a control that appears on one
// chart and not the next reads as a bug rather than as a decision. Everything
// that decides what a combination MEANS lives in this file, so the rules can be
// checked in one place and broken in one place.
//
// ⚠️⚠️ ONE METRIC PER CHART. NEVER A SECOND Y-AXIS. The metric switch REPLACES
// the measure; it never adds a scale. Two scales on one chart is the single most
// common way a performance report misleads, and NO user preference may opt into
// it — there is no combination in `chartLayout` that produces one, and
// `resolveAxis` returns exactly one scale for every input.
//
// ⚠️⚠️ THE FORM FOLLOWS THE DATA'S JOB, and the reader may override it. A
// magnitude is a bar; a ratio over time is a line. The override is remembered
// PER METRIC, so choosing lines for spend does not silently make cost per lead
// a bar.

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * What a chart plots. ⚠️ `share` exists ONLY on a `channel` chart — it is the
 * one view where two different measures appear together, and only because both
 * are then percentages of their own total.
 */
export type ChartMetric =
  | 'leads'
  | 'spend'
  | 'costPerLead'
  | 'share'
  /**
   * ⚠️ THE DELIVERY BAND'S FIVE (2026-09-01). They are ChartMetrics rather than
   * a parallel vocabulary so that every honesty rule in this file — never a
   * second axis, never stacked unless additive, zero-based for bars, a gap is
   * not a zero — applies to them by construction instead of being re-derived in
   * a second component and kept in step by hand.
   */
  | DeliveryMetricKey
  /**
   * ⚠️ THE CRM'S SIX (2026-09-01). Same reason as the delivery five: they are
   * ChartMetrics rather than a parallel vocabulary, so never-stacked,
   * zero-based-bars, a-gap-is-not-a-zero and the good/bad direction all hold
   * without being re-derived in a second component.
   *
   * ⚠️⚠️ THEIR VALUES ARE NOT READ BY `valueAt`. A CRM bucket is a different
   * SHAPE (`PortalCrmSeriesPoint`), and two of the six need AD SPEND as well —
   * so `crmValueAt` reads them, and `PlottableMetric` excludes them so the
   * compiler enforces it rather than a comment.
   */
  | CrmMetricKey
  /**
   * ⚠️ WEBSITE ANALYTICS (GA4, 2026-09-27) — the CRM precedent again: a
   * different point shape (`PortalWebsitePoint`), read by `websiteValueAt`, and
   * excluded from `PlottableMetric` so the compiler enforces it. Every rate is
   * re-derived per bucket from that bucket's sums.
   */
  | WebsiteMetricKey;

/** The delivery ratios. ⚠️ Their axis may float; a count's may not. */
const RATIO_METRICS: ReadonlySet<ChartMetric> = new Set<ChartMetric>([
  'costPerLead',
  'ctr',
  'costPerThousand',
  'costPerClick',
  'crmCloseRate',
  'crmCostPerDigitalLead',
  'crmCostPerSale',
  'webBounceRate',
  'webAvgDuration',
]);

/** ⚠️ Metrics where a FALLING number is the good news. */
const COST_METRICS: ReadonlySet<ChartMetric> = new Set<ChartMetric>([
  'costPerLead',
  'costPerThousand',
  'costPerClick',
  // ⚠️ A falling cost per sale is the best news on the page, exactly as a
  // falling cost per lead is. ⚠️ `crmCloseRate` is NOT here — a RISING close
  // rate is the good direction, so it follows the default.
  'crmCostPerDigitalLead',
  'crmCostPerSale',
  // ⚠️ A falling bounce rate is the good direction. Average duration is not
  // here — longer visits follow the default.
  'webBounceRate',
]);

export type ChartForm = 'bar' | 'line';

/** 12 rolling months, or the selected reporting period. */
export type ChartGrain = 'months' | 'period';

/**
 * What the series MEAN to each other, which is what decides whether stacking is
 * honest:
 *   · `comparison` — the same measure over two runs of time (this year against
 *     last, or a single period). The parts are NOT parts of a whole.
 *   · `channel` — Meta and Google. For spend and leads the parts genuinely sum
 *     to the whole; for a ratio they do not.
 */
export type ChartKind = 'comparison' | 'channel';

export type ChartLayout =
  /** One bar per bucket. */
  | 'single'
  /** Two bars side by side. ⚠️ What a ratio gets when the reader asks for bars. */
  | 'grouped'
  /** One bar per bucket, split inside. Only where the parts sum to the whole. */
  | 'stacked'
  /** Two 100% stacks per bucket — the spend split, then the lead split. */
  | 'paired'
  | 'line';

export interface ChartPoint {
  /**
   * The bucket KEY — `YYYY-MM` or `YYYY-MM-DD`.
   *
   * ⚠️⚠️ CARRIED SO AN AXIS CAN RE-FORMAT WITHOUT PARSING `label` BACK (§3,
   * 2026-09-03). The axis wants `Mar ’26` where the tooltip and the prose want
   * `March 2026`; deriving the short form from the formatted string would tie
   * the axis to whatever `monthLabel` happens to print, which is precisely the
   * coupling the two formatters exist to avoid.
   *
   * ⚠️ REQUIRED, so a future construction site cannot omit it and leave the
   * axis silently falling back to the long form.
   */
  bucket: string;
  label: string;
  value: number | null;
  coverage: PortalBucketCoverage;
  /** ClubWise only: the month's upload runs to this day (YYYY-MM-DD); the tooltip says so. */
  partialTo?: string;
}

export interface ChartSeries {
  label: string;
  /**
   * Which FIXED data colour. ⚠️ Never a brand accent — Snap's brand colour is
   * red, and a red performance chart reads as loss on a page of results.
   * `baseline` is the grey dashed last-year line: it is a BASELINE, not a third
   * category, and it must not take channel B's colour, which means "Google"
   * everywhere else on this report.
   */
  role: 'a' | 'b' | 'baseline';
  points: ChartPoint[];
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

/**
 * ⚠️ A MAGNITUDE IS A BAR; A RATIO OVER TIME IS A LINE. `share` is a ratio but
 * its bar form is the interesting one — two 100% stacks side by side IS the
 * "is Meta returning more than it costs" comparison — so it defaults to bar.
 */
export const DEFAULT_FORM: Readonly<Record<ChartMetric, ChartForm>> = {
  leads: 'bar',
  spend: 'bar',
  costPerLead: 'line',
  share: 'bar',
  // ⚠️ The delivery five follow the SAME rule, not a new one: counts are
  // magnitudes and get bars; the three ratios get lines, exactly as cost per
  // lead does.
  impressions: 'bar',
  clicks: 'bar',
  ctr: 'line',
  costPerThousand: 'line',
  costPerClick: 'line',
  // ⚠️ The CRM six follow the SAME rule again, not a third one: counts are
  // magnitudes and get bars; the three ratios get lines.
  crmLeads: 'bar',
  crmDigitalLeads: 'bar',
  crmStaffLeads: 'bar',
  crmSales: 'bar',
  crmCloseRate: 'line',
  crmCostPerDigitalLead: 'line',
  crmCostPerSale: 'line',
  // Same rule: counts are bars, the two derived figures are lines.
  webSessions: 'bar',
  webNewUsers: 'bar',
  webBounceRate: 'line',
  webAvgDuration: 'line',
};

/**
 * The form to show for `metric`, honouring an override the reader already made
 * FOR THAT METRIC.
 *
 * ⚠️ Per metric, not per chart. Choosing lines for spend must not silently make
 * cost per lead a bar — they are different questions, and the default encodes
 * which shape answers each.
 */
export function resolveForm(
  metric: ChartMetric,
  touched: Readonly<Partial<Record<ChartMetric, ChartForm>>>,
): ChartForm {
  return touched[metric] ?? DEFAULT_FORM[metric];
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/**
 * What a (kind × metric × form × series-count) combination actually draws.
 *
 * ⚠️⚠️ COST PER LEAD IS NEVER STACKED. Stacking two cost-per-lead figures adds
 * numbers that cannot be added: A$35 of Meta plus A$44 of Google is not A$79 of
 * anything. The grouped form reads a trend worse than the line does, but it is
 * not FALSE — and the footnote says so while it is active.
 *
 * ⚠️ Stacking is licensed by `kind === 'channel'` AND an additive metric, not by
 * the form switch alone: two runs of time (this year, last year) never stack,
 * whatever is being measured, because 2026 + 2025 is not a quantity.
 */
export function chartLayout(
  kind: ChartKind,
  metric: ChartMetric,
  form: ChartForm,
  seriesCount: number,
): ChartLayout {
  if (form === 'line') return 'line';
  if (kind === 'channel') {
    if (metric === 'share') return 'paired';
    // ⚠️ The additive gate. costPerLead falls through to 'grouped' by design.
    if (metric === 'spend' || metric === 'leads') return 'stacked';
    return 'grouped';
  }
  return seriesCount > 1 ? 'grouped' : 'single';
}

/** How a not-yet-finished bucket is marked, per layout. */
export type PartialTreatment =
  /** A hatched fill. */
  | 'hatch'
  /** A faded fill plus a dashed cap. */
  | 'fade'
  /** A dashed final segment plus a hollow final dot. */
  | 'dash';

/**
 * ⚠️⚠️ HATCHING HAS EXACTLY ONE MEANING IN THIS REPORT: "this period is
 * incomplete." It briefly also meant "Google" in the mockup, and the result was
 * that every COMPLETE month read as provisional. One texture, one meaning — so a
 * stacked or paired chart, which already carries two fills, marks the partial
 * bucket by fading it and capping it with a dash rather than by adding a texture
 * that would then be competing with the channel split.
 */
export function partialTreatment(layout: ChartLayout): PartialTreatment {
  if (layout === 'line') return 'dash';
  if (layout === 'stacked' || layout === 'paired') return 'fade';
  return 'hatch';
}

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/**
 * What `valueAt` can read off a `PortalSeriesPoint`.
 *
 * ⚠️ `share` is composed elsewhere, from two of these. ⚠️⚠️ THE CRM SIX ARE
 * EXCLUDED BY TYPE, not by convention: a CRM bucket is a different shape and two
 * of its metrics need AD SPEND, so `crmValueAt` reads them. Passing one here is
 * a compile error rather than a silent `undefined`.
 */
export type PlottableMetric = Exclude<ChartMetric, 'share' | CrmMetricKey | WebsiteMetricKey>;

/**
 * Read one metric off a series point. ⚠️ null stays null — never coerced to 0.
 *
 * ⚠️ The delivery ratios are DELEGATED to `delivery.ts` rather than recomputed
 * here. One derivation, so the chart cannot plot a click-through rate that
 * disagrees with the figure printed above it.
 */
export function valueAt(point: PortalSeriesPoint, metric: PlottableMetric): number | null {
  if (metric === 'leads') return point.leads;
  if (metric === 'spend') return point.spend;
  if (metric === 'costPerLead') return point.costPerLead;
  return deliveryValueAt(point, metric);
}

/** A run of series points reduced to what a chart needs. */
export function toChartPoints(
  series: readonly PortalSeriesPoint[],
  metric: PlottableMetric,
): ChartPoint[] {
  return series.map((p) => ({
    bucket: p.bucket,
    label: p.label,
    value: valueAt(p, metric),
    coverage: p.coverage,
  }));
}

/**
 * A's share of the A+B total, as a percentage.
 *
 * ⚠️ NULL rather than 0 when either side is unmeasured OR the total is zero. A
 * share of nothing is undefined, not "0% went to Meta" — and on a share chart a
 * zero is a strong claim: it says all of it went to the other channel.
 */
export function shareOfTotal(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  const total = a + b;
  if (total === 0) return null;
  return round1((a / total) * 100);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ---------------------------------------------------------------------------
// Axis
// ---------------------------------------------------------------------------

export interface ChartScale {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

/**
 * Nice-rounded ticks, so the reader can value a bar without hovering it.
 *
 * ⚠️ A chart with no scale forces every question through the tooltip, which is
 * exactly the friction a report exists to remove.
 */
export function niceScale(lo: number, hi: number, tickCount = 4): ChartScale {
  let high = hi;
  if (high === lo) high = lo + 1;
  const raw = (high - lo) / tickCount;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= raw) ?? 10 * mag;
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(high / step) * step;
  const ticks: number[] = [];
  for (let v = min, guard = 0; v <= max + step * 1e-9 && guard < 40; v += step, guard += 1) {
    // Floating-point accumulation would otherwise print 0.30000000000000004.
    ticks.push(Math.round(v * 1e6) / 1e6);
  }
  return { min, max, step, ticks };
}

/**
 * ⚠️⚠️ ZERO-BASED FOR EVERY BAR AND EVERY MAGNITUDE. A bar's LENGTH is its
 * value, so a truncated axis makes a 5% difference look like a doubling. A
 * RATIO drawn as a LINE may start above zero, because the reader is comparing
 * levels rather than areas — and pinning cost per lead to zero flattens the
 * variation the chart exists to show.
 */
export function isZeroBased(metric: ChartMetric, form: ChartForm): boolean {
  if (form === 'bar') return true;
  // `share` is a ratio too, but its axis is the fixed 0-100% one below.
  return !RATIO_METRICS.has(metric);
}

/**
 * The one scale a chart draws against. ⚠️ Exactly one — there is no branch here
 * that returns two, which is what makes "never a second y-axis" structural
 * rather than remembered.
 */
export function resolveAxis(
  layout: ChartLayout,
  metric: ChartMetric,
  form: ChartForm,
  series: readonly ChartSeries[],
): ChartScale {
  if (metric === 'share') {
    // Fixed, because a share axis that adapts to the data invites the reader to
    // misjudge how far from an even split they are.
    return { min: 0, max: 100, step: 25, ticks: [0, 25, 50, 75, 100] };
  }
  if (layout === 'stacked') {
    // ⚠️ The DRAWN heights, not the honest totals — a partial column is drawn and
    // must fit inside the plot. See `stackedDrawnHeights`.
    const heights = stackedDrawnHeights(series);
    const hi = heights.length ? Math.max(...heights) : 1;
    return niceScale(0, hi * 1.05);
  }
  const values = series.flatMap((s) => s.points.map((p) => p.value)).filter(isNumber);
  if (!values.length) return niceScale(0, 1);
  const hi = Math.max(...values) * 1.05;
  const lo = isZeroBased(metric, form) ? 0 : Math.min(...values) * 0.92;
  return niceScale(lo, hi);
}

/**
 * ⚠️⚠️ HOW MUCH OF A STACKED BUCKET WE ACTUALLY KNOW. `complete` — every part
 * measured. `partial` — some measured, some not. `none` — nothing measured, so
 * there is no column at all and the gap band already says so.
 *
 * ⚠️ DECIDED BY `coverage`, NEVER BY A NULL VALUE. A channel sitting at a
 * MEASURED ZERO means the other channel alone IS the total and the column is
 * complete; inferring "unmeasured" from the absence of a number would put an
 * unknown-remainder marker on a month we know everything about.
 *
 * ⚠️ A complete-coverage point carrying a null value is still not drawable, and
 * is treated as unknown. On a stacked chart the metric is always additive
 * (spend or leads), where that combination should not arise — but reading a
 * null as zero is exactly the failure this file exists to prevent, so it is
 * handled rather than assumed away.
 */
export type StackCoverage = 'complete' | 'partial' | 'none';

export function stackedCoverageAt(series: readonly ChartSeries[], index: number): StackCoverage {
  if (!series.length) return 'none';
  const points = series.map((s) => s.points[index]);
  const known = points.filter(
    (p) => p !== undefined && p.coverage !== 'no-data' && p.value !== null,
  );
  if (known.length === 0) return 'none';
  return known.length === points.length ? 'complete' : 'partial';
}

/**
 * The per-bucket heights a stacked chart actually DRAWS.
 *
 * ⚠️⚠️ THIS IS NOT `stackedTotalAt`, AND THE DIFFERENCE IS THE POINT. That one
 * answers "what was the total", and returns NULL for a partial bucket because
 * the total is genuinely unknown — the tooltip reads it, and must never state a
 * total it does not have. This answers "how tall is the column we drew", which
 * for a partial bucket is the sum of the parts we DO know.
 *
 * ⚠️ The axis needs THIS one. Scaling to the honest totals would leave a partial
 * column — which is drawn — free to overflow the top of the plot.
 */
export function stackedDrawnHeights(series: readonly ChartSeries[]): number[] {
  if (!series.length) return [];
  const n = series[0]?.points.length ?? 0;
  const out: number[] = [];
  for (let i = 0; i < n; i += 1) {
    if (stackedCoverageAt(series, i) === 'none') continue;
    let sum = 0;
    for (const s of series) {
      const p = s.points[i];
      if (p && p.coverage !== 'no-data' && p.value !== null) sum += p.value;
    }
    out.push(sum);
  }
  return out;
}

/** The stacked total AT one bucket, or null when any part is unmeasured. */
export function stackedTotalAt(series: readonly ChartSeries[], index: number): number | null {
  const parts = series.map((s) => s.points[index]?.value ?? null);
  if (!parts.length || parts.some((v) => v === null)) return null;
  return parts.reduce<number>((sum, v) => sum + (v as number), 0);
}

function isNumber(v: number | null): v is number {
  return v !== null;
}

// ---------------------------------------------------------------------------
// Gaps, partials, density
// ---------------------------------------------------------------------------

/**
 * The runs of buckets that were NOT MEASURED, as inclusive index ranges.
 *
 * ⚠️ Keyed on `coverage === 'no-data'`, NOT on a null value. A measured bucket
 * with spend and no leads has a null cost per lead and is emphatically not a
 * gap — see `series.ts`'s `isSpendWithoutLeads` for why the two must never be
 * collapsed.
 */
export function gapRuns(points: readonly ChartPoint[]): Array<{ from: number; to: number }> {
  const runs: Array<{ from: number; to: number }> = [];
  let start: number | null = null;
  points.forEach((p, i) => {
    if (p.coverage === 'no-data') {
      if (start === null) start = i;
      return;
    }
    if (start !== null) {
      runs.push({ from: start, to: i - 1 });
      start = null;
    }
  });
  if (start !== null) runs.push({ from: start, to: points.length - 1 });
  return runs;
}

/**
 * The runs a line may be drawn continuously through.
 *
 * ⚠️⚠️ A LINE BREAKS ACROSS AN UNMEASURED SPAN AND ONLY THERE. Drawing across a
 * period we never measured is a confident claim about nothing — the graphical
 * form of inventing data. WITHIN a run the line connects across a null, because
 * a measured bucket with spend and no leads is a KNOWN state and breaking there
 * looks like a rendering fault to a reader who can see data either side.
 *
 * Returns inclusive index ranges. Mirrors `series.ts`'s `connectableSegments`
 * for the same reason, on the chart's own point shape.
 */
export function lineSegments(points: readonly ChartPoint[]): Array<{ from: number; to: number }> {
  const segments: Array<{ from: number; to: number }> = [];
  let start: number | null = null;
  points.forEach((p, i) => {
    if (p.coverage === 'no-data') {
      if (start !== null) {
        segments.push({ from: start, to: i - 1 });
        start = null;
      }
      return;
    }
    if (start === null) start = i;
  });
  if (start !== null) segments.push({ from: start, to: points.length - 1 });
  return segments;
}

/**
 * Which buckets are not finished yet.
 *
 * ⚠️ Derived from each point's OWN coverage rather than from "the last one", so
 * a year-on-year chart marks August 2026 partial while leaving August 2025 — a
 * complete month — alone. Hardcoding "the last index" would mark both.
 */
export function partialIndexes(points: readonly ChartPoint[]): number[] {
  const out: number[] = [];
  points.forEach((p, i) => {
    if (p.coverage === 'in-progress') out.push(i);
  });
  return out;
}

/**
 * ⚠️ A DOT PER POINT ONLY HELPS WHILE THE POINTS READ APART. Across a 90-day
 * line they merge into a caterpillar and the line stops reading as a line, so
 * the radius follows the spacing and below ~18px there are no permanent dots at
 * all. Hit targets do not depend on this — the whole column is the hover target.
 */
export function dotRadius(bandWidth: number): number {
  if (bandWidth >= 34) return 3.2;
  if (bandWidth >= 18) return 2.2;
  return 0;
}

/**
 * The point that must carry a dot whatever the density says.
 *
 * ⚠️ THE LAST MEASURED POINT IS ALWAYS MARKED — it is the number the reader came
 * for, and an unmarked line end reads as a cropped chart. (The hovered point is
 * marked too, but that is a render-time concern rather than a plan one.)
 */
export function anchoredDotIndexes(points: readonly ChartPoint[]): number[] {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const v = points[i]?.value;
    if (v !== null && v !== undefined) return [i];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Change
// ---------------------------------------------------------------------------

/** Percentage change from `previous` to `current`, or null when incomparable. */
export function changeBetween(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  // ⚠️ A change FROM zero has no percentage. Reporting +Infinity — or +100% —
  // would manufacture a figure out of an undefined one.
  if (previous === 0) return null;
  return round1(((current - previous) / previous) * 100);
}

/**
 * ⚠️⚠️ THE GOOD/BAD DIRECTION INVERTS FOR COST METRICS. A falling cost per lead
 * is the best news on the page; showing it red tells the client the opposite of
 * the truth. Spend is NEUTRAL — a client who raised their budget on purpose
 * should not be shown it in red either.
 *
 * ⚠️ `share` is neutral too, and deliberately: the split is zero-sum, so
 * praising one channel condemns the other and neither claim is one the number
 * can support.
 */
export function changeTone(metric: ChartMetric, pct: number | null): 'good' | 'bad' | 'neutral' {
  if (pct === null || pct === 0) return 'neutral';
  const direction = metricDirection(metric);
  if (direction === 'neutral') return 'neutral';
  return (pct > 0) === (direction === 'good-up') ? 'good' : 'bad';
}

/**
 * ⚠️⚠️ ONE DEFINITION OF WHICH WAY IS GOOD, for every metric on the report.
 * `changeTone` (the chart tooltip) and `portalMetricTone` (the hero's delta)
 * both read it, so a falling cost cannot be green in one place and red in the
 * other — which is the single number a client most wants to fall.
 *
 * ⚠️ SPEND IS NEUTRAL and so is `share`: a client who raised their budget on
 * purpose should not be shown it in red, and a zero-sum split praises one
 * channel only by condemning the other.
 *
 * ⚠️ Every COST metric is good-DOWN, including the CRM's two — a price is a
 * price whatever it is a price for. `crmCloseRate` is NOT a cost: a rising close
 * rate is the good direction, so it falls through to the default.
 */
export function metricDirection(metric: ChartMetric): 'good-up' | 'good-down' | 'neutral' {
  if (metric === 'spend' || metric === 'share') return 'neutral';
  return COST_METRICS.has(metric) ? 'good-down' : 'good-up';
}
